import React from "react";
import { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import useGet from "../../hooks/useGet";
import PortalDealCard from "./PortalDealCard";
import Button from "../ui/Button";
import LoadingOverlay from "../shared/LoadingOverlay";
import PortalDealListFilters from "./PortalDealListFilters";
import portalContext from "../../store/PortalContext";
import AuthContext from "../../store/AuthContext";
import envVariables from "../../data/envVariables";

const abbrevMap = {
    "Request a quote": "RFQ",
    "Know Your Customer": "KYC",
    "Ready for quote": "RFQ",
    "Out to market": "OTM",
    "Broker review": "BR",
    Quoted: "QUOTED",
    "Handed to Ops": "HO",
    "Preliminary brief": "PB",
    "Passports & catering": "PC",
    "Confirmed with operator": "CWO",
    "Final brief": "FB",
};

const PortalRequestsListView = ({ stageOptions, stageGroups }) => {
    const portalCtx = useContext(portalContext);
    const authCtx = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const [currentFilter, setCurrentFilter] = useState(() => localStorage.getItem("portalDealFilter") || "all");
    const [filterByMember, setFilterByMember] = useState(null);
    const [hasFilter, setHasFilter] = useState(false);
    const [filteredResults, setFilteredResults] = useState([]);

    const resolveInitialStage = () => {
        const fromUrl = searchParams.get("stage");
        if (fromUrl && stageOptions?.includes(fromUrl)) return fromUrl;
        return stageOptions?.[0] ?? null;
    };

    const resolveInitialGroupIndex = () => {
        const raw = searchParams.get("group");
        const idx = raw !== null ? parseInt(raw, 10) : 0;
        if (!stageGroups?.length) return 0;
        if (!Number.isNaN(idx) && idx >= 0 && idx < stageGroups.length) return idx;
        return 0;
    };

    // ── Non-grouped (stageOptions) ──────────────────────────────────────────
    const [selectedStage, setSelectedStage] = useState(resolveInitialStage);
    const [stageDealUrl, setStageDealUrl] = useState(null);
    const { loading: stageLoading, returnData: stageData, error: stageError } = useGet(stageDealUrl);

    useEffect(() => {
        if (stageGroups || !selectedStage) return;
        if (searchParams.get("stage") !== selectedStage) {
            setSearchParams({ stage: selectedStage }, { replace: true });
        }
    }, [selectedStage, stageGroups, searchParams, setSearchParams]);

    useEffect(() => {
        if (!stageGroups && selectedStage) {
            setStageDealUrl(
                currentFilter === "Mine"
                    ? `/api/portal/deals-by-stage-by-owner?stageLabel=${selectedStage}&ownerId=${portalCtx.id}`
                    : `/api/portal/deals-by-stage?stageLabel=${selectedStage}&f=${currentFilter}`
            );
        }
    }, [selectedStage, currentFilter, stageGroups, portalCtx.id]);

    // ── Grouped (stageGroups) ───────────────────────────────────────────────
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(resolveInitialGroupIndex);

    useEffect(() => {
        if (!stageGroups) return;
        if (searchParams.get("group") !== String(selectedGroupIndex)) {
            setSearchParams({ group: String(selectedGroupIndex) }, { replace: true });
        }
    }, [selectedGroupIndex, stageGroups, searchParams, setSearchParams]);
    const [groupData, setGroupData] = useState(null);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupError, setGroupError] = useState(false);

    useEffect(() => {
        if (!stageGroups) return;

        const group = stageGroups[selectedGroupIndex];
        if (!group) return;

        setGroupLoading(true);
        setGroupError(false);
        setGroupData(null);

        const config = authCtx?.token
            ? { headers: { "Content-type": "application/json", Authorization: `Bearer ${authCtx.token}` } }
            : {};

        const urls = group.stages.map((stage) =>
            currentFilter === "Mine"
                ? `/api/portal/deals-by-stage-by-owner?stageLabel=${stage}&ownerId=${portalCtx.id}`
                : `/api/portal/deals-by-stage?stageLabel=${stage}&f=${currentFilter}`
        );

        Promise.all(urls.map((url) => axios.get(envVariables.apiEndpoint + url, config)))
            .then((responses) => {
                setGroupData(responses.flatMap((r) => r.data || []));
                setGroupLoading(false);
            })
            .catch(() => {
                setGroupError(true);
                setGroupData([]);
                setGroupLoading(false);
            });
    }, [selectedGroupIndex, currentFilter, stageGroups, authCtx?.token, portalCtx.id]);

    // ── Shared ──────────────────────────────────────────────────────────────
    const activeData = stageGroups ? groupData : stageData;
    const activeLoading = stageGroups ? groupLoading : stageLoading;
    const activeError = stageGroups ? groupError : stageError;
    const activeLabel = (stageGroups ? stageGroups[selectedGroupIndex]?.label : selectedStage)?.replace(
        /\b[a-z]/g,
        (letter) => letter.toUpperCase()
    );

    useEffect(() => {
        setFilteredResults(activeData);

        if (activeData && filterByMember) {
            setFilteredResults(
                activeData.filter((d) => {
                    if (filterByMember.zohoModule === "Leads") return filterByMember.zohoId === d["lead_Name.Id"];
                    if (filterByMember.zohoModule === "Contacts") return filterByMember.zohoId === d["contact_Name.Id"];
                    return false;
                })
            );
        }
    }, [activeData, filterByMember]);

    const updateSelectedClientLead = (newLead) => {
        setHasFilter(!!newLead);
        setFilterByMember(newLead);
    };

    const updateSelectedViewFilter = (newFilter) => {
        setCurrentFilter(newFilter);
        localStorage.setItem("portalDealFilter", newFilter);

        if (!stageGroups && selectedStage) {
            setStageDealUrl(
                newFilter === "Mine"
                    ? `/api/portal/deals-by-stage-by-owner?stageLabel=${selectedStage}&ownerId=${portalCtx.id}`
                    : `/api/portal/deals-by-stage?stageLabel=${selectedStage}&f=${newFilter}`
            );
        }
    };

    const renderStageButtons = () => {
        if (stageGroups) {
            return stageGroups.map((group, gi) => (
                <Button
                    key={gi}
                    onClick={() => {
                        setSelectedGroupIndex(gi);
                        setSearchParams({ group: String(gi) }, { replace: true });
                    }}
                    classNames={[gi === selectedGroupIndex ? "button--stage-active" : "button--stage-inactive"]}
                >
                    {group.label}
                </Button>
            ));
        }

        return stageOptions.map((opt, i) => (
            <Button
                key={i}
                onClick={() => {
                    setSelectedStage(opt);
                    setSearchParams({ stage: opt }, { replace: true });
                }}
                classNames={[opt === selectedStage ? "button--stage-active" : "button--stage-inactive"]}
            >
                <span className="button--stage-full">{opt}</span>
                <span className="button--stage-short">{abbrevMap[opt] || opt}</span>
            </Button>
        ));
    };

    const displayData = hasFilter && filteredResults ? filteredResults : activeData;

    return (
        <>
            <div className="portal__filters-container">
                <PortalDealListFilters
                    onDidSelectMember={updateSelectedClientLead}
                    onDidChangeFilter={updateSelectedViewFilter}
                    currentFilter={currentFilter}
                    stageButtons={<div className="portal__stagefilter_wrapper">{renderStageButtons()}</div>}
                />
            </div>

            {!activeLoading && !activeError && displayData?.length > 0 && (
                <div className="portal__dealcards">
                    {displayData.map((deal) => (
                        <PortalDealCard key={deal.id} deal={deal} />
                    ))}
                </div>
            )}

            {activeLoading && <LoadingOverlay />}

            {!activeLoading && activeError && (
                <div className="portal__empty-state" role="alert">
                    <h2>Could not load deals</h2>
                    <p>Please refresh the page to try again.</p>
                </div>
            )}

            {!activeLoading &&
                !activeError &&
                activeData != null &&
                (activeData.length === 0 || (hasFilter && filteredResults?.length === 0)) && (
                    <div className="portal__empty-state" role="status">
                        <h2>
                            {hasFilter || currentFilter.toLowerCase() !== "all"
                                ? "No matching deals"
                                : `No deals in ${activeLabel}`}
                        </h2>
                        <p>
                            {hasFilter || currentFilter.toLowerCase() !== "all"
                                ? `No deals match your filters in ${activeLabel}. Try changing or resetting your filters.`
                                : "Deals will appear here when they reach this stage. You can check another tab in the meantime."}
                        </p>
                    </div>
                )}
        </>
    );
};

export default PortalRequestsListView;
